import { UtensilsCrossed, DollarSign, ShoppingBag, Users2 } from 'lucide-react';
import { DashboardStats } from '../../services/dashboard.service';

interface StatsCardsProps {
  indices: number[];
  stats: DashboardStats | null;
  loading?: boolean;
  onNavigate?: (page: string) => void;
}

export function StatsCards({ indices, stats, loading = false, onNavigate }: StatsCardsProps) {
  // Format revenue to show as "126k" or "1.2M"
  const formatRevenue = (revenue: number | undefined): string => {
    if (!revenue || revenue === 0) {
      return '0';
    }
    if (revenue >= 1000000) {
      return `${(revenue / 1000000).toFixed(1)}M`;
    }
    if (revenue >= 1000) {
      return `${(revenue / 1000).toFixed(0)}k`;
    }
    return revenue.toString();
  };

  // Format percentage change
  const formatChange = (change: number): string => {
    return `${Math.abs(change).toFixed(1)}% (30 days)`;
  };

  // Define all stats cards
  const allStats = [
    {
      icon: UtensilsCrossed,
      value: stats?.totalMenus?.toString() || '0',
      label: 'Total Menus',
      change: formatChange(stats?.revenueChange || 0),
      changePositive: (stats?.revenueChange || 0) >= 0,
      bgColor: 'bg-indigo-50',
      iconColor: 'text-indigo-600',
      navigateTo: 'menuTypes',
    },
    {
      icon: DollarSign,
      value: formatRevenue(stats?.totalRevenue),
      label: 'Total Revenue',
      change: formatChange(stats?.revenueChange || 0),
      changePositive: (stats?.revenueChange || 0) >= 0,
      bgColor: 'bg-indigo-50',
      iconColor: 'text-indigo-600',
      navigateTo: null,
    },
    {
      icon: ShoppingBag,
      value: stats?.totalOrders?.toString() || '0',
      label: 'Total Orders',
      change: formatChange(stats?.ordersChange || 0),
      changePositive: (stats?.ordersChange || 0) >= 0,
      bgColor: 'bg-indigo-50',
      iconColor: 'text-indigo-600',
      navigateTo: 'orders',
    },
    {
      icon: Users2,
      value: stats?.totalCustomers?.toString() || '0',
      label: 'Total Customers',
      change: formatChange(stats?.customersChange || 0),
      changePositive: (stats?.customersChange || 0) >= 0,
      bgColor: 'bg-indigo-50',
      iconColor: 'text-indigo-600',
      navigateTo: 'customers',
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-4">
      {indices.map((index) => {
        const stat = allStats[index];
        return (
          <button
            key={index}
            onClick={() => stat.navigateTo && onNavigate?.(stat.navigateTo)}
            disabled={loading}
            className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-800 transition-all duration-200 hover:shadow-lg hover:-translate-y-1 cursor-pointer text-left disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="flex items-start gap-4">
              <div
                className={`${stat.bgColor} dark:bg-indigo-900/30 ${stat.iconColor} dark:text-indigo-400 p-3 rounded-xl flex-shrink-0`}
              >
                <stat.icon className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                {loading ? (
                  <>
                    <div className="h-8 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2" />
                    <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2" />
                    <div className="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                  </>
                ) : (
                  <>
                    <div className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                      {stat.value}
                    </div>
                    <div className="text-sm text-gray-900 dark:text-gray-300 font-medium mb-1">
                      {stat.label}
                    </div>
                    <div
                      className={`text-xs flex items-center gap-1 ${
                        stat.changePositive
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                      }`}
                    >
                      <span>{stat.changePositive ? '↗' : '↘'}</span>
                      <span>{stat.change}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}